import { createReducer, on } from '@ngrx/store';

import { TenantSettingsResponse } from '@core/api/tenant/tenant.models';
import { ThemePreference } from '@core/api/user-preferences/user-preferences.models';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';
import { mapUiThemeApiModelToStoreModel } from '@domain/theme/theme-models.mappings';
import { ThemeStoreModel } from '@domain/theme/theme.models';

import {
  exitImpersonation,
  impersonationFailure,
  impersonationSuccess,
  loadTenantSettings,
  loadTenantSettingsFailure,
  loadTenantSettingsSuccess,
  loadTenantTheme,
  loadTenantThemeFailure,
  loadTenantThemeSuccess,
  loadUserPreferences,
  loadUserPreferencesFailure,
  loadUserPreferencesSuccess,
  renewImpersonation,
  saveUserPreferences,
  saveUserPreferencesFailure,
  saveUserPreferencesSuccess,
  setInitialTokenAndPermissions,
  setSystemPrefersDark,
  setThemePreference,
  setUpdatedTokenAndPermissions,
  startImpersonation,
} from './auth.actions';
import { MOCK_THEME } from './mock-theme';

export interface AuthState {
  accessToken?: string | null;
  impersonationToken?: string;
  impersonationTargetUserId?: string;
  impersonationPermissions?: PermissionStoreModelEnum[];
  impersonationName?: string;
  impersonationOrganisationId?: string;
  impersonationIsLoading?: boolean;
  initialImpersonationIsLoading?: boolean;
  impersonationError?: string;
  name?: string | null;
  organisationId?: string;
  refreshTokenExpiresTimestamp?: string | null;
  permissions?: PermissionStoreModelEnum[];
  lightTheme?: ThemeStoreModel;
  darkTheme?: ThemeStoreModel;
  themeIsLoading?: boolean;
  themeError?: string;
  themePreference?: ThemePreference;
  systemPrefersDark?: boolean;
  userPreferencesIsLoading?: boolean;
  userPreferencesError?: string;
  isSavingUserPreferences?: boolean;
  tenantSettings?: TenantSettingsResponse;
  tenantSettingsIsLoading?: boolean;
  tenantSettingsError?: string;
}

export const initialState: AuthState = {};

export const authFeatureKey = 'auth';

export const authReducer = createReducer(
  initialState,

  on(setInitialTokenAndPermissions, (state, { token, refreshTokenExpiresTimestamp, permissions, name, organisationId, impersonation }) => ({
    ...state,
    accessToken: token,
    refreshTokenExpiresTimestamp,
    permissions,
    name,
    organisationId,
    impersonationToken: impersonation?.token,
    impersonationTargetUserId: impersonation?.targetUserId,
    impersonationPermissions: impersonation?.permissions,
    impersonationName: impersonation?.name,
    impersonationOrganisationId: impersonation?.organisationId,
  })),
  on(setUpdatedTokenAndPermissions, (state, { token, refreshTokenExpiresTimestamp, permissions, name, organisationId }) => ({
    ...state,
    accessToken: token,
    refreshTokenExpiresTimestamp,
    permissions,
    name,
    organisationId,
  })),
  on(startImpersonation, (state) => ({
    ...state,
    impersonationIsLoading: true,
    initialImpersonationIsLoading: true,
    impersonationError: undefined,
  })),
  on(renewImpersonation, (state) => ({ ...state, impersonationIsLoading: true, impersonationError: undefined })),
  on(impersonationSuccess, (state, { token, targetUserId, permissions, name, organisationId, navigateHome }) => ({
    ...state,
    impersonationToken: token,
    impersonationTargetUserId: targetUserId,
    impersonationPermissions: permissions,
    impersonationName: name,
    impersonationOrganisationId: organisationId,
    impersonationIsLoading: false,
    initialImpersonationIsLoading: false,
    impersonationError: undefined,
    ...(navigateHome ? { tenantSettings: undefined, tenantSettingsError: undefined } : {}),
  })),
  on(impersonationFailure, (state, { error }) => ({
    ...state,
    impersonationIsLoading: false,
    initialImpersonationIsLoading: false,
    impersonationError: error,
  })),
  on(exitImpersonation, (state) => ({
    ...state,
    impersonationToken: undefined,
    impersonationTargetUserId: undefined,
    impersonationPermissions: undefined,
    impersonationName: undefined,
    impersonationOrganisationId: undefined,
    impersonationIsLoading: false,
    initialImpersonationIsLoading: false,
    impersonationError: undefined,
    tenantSettings: undefined,
    tenantSettingsError: undefined,
  })),
  on(loadTenantTheme, (state) => ({ ...state, themeIsLoading: true })),
  on(loadTenantThemeSuccess, (state, { lightTheme, darkTheme }) => ({
    ...state,
    lightTheme,
    darkTheme,
    themeIsLoading: false,
  })),
  on(loadTenantThemeFailure, (state, { error }) => ({
    ...state,
    themeError: error,
    themeIsLoading: false,
    lightTheme: mapUiThemeApiModelToStoreModel(MOCK_THEME),
    darkTheme: mapUiThemeApiModelToStoreModel(MOCK_THEME),
  })),
  on(loadTenantSettings, (state) => ({ ...state, tenantSettingsIsLoading: true })),
  on(loadTenantSettingsSuccess, (state, { response }) => ({ ...state, tenantSettings: response, tenantSettingsIsLoading: false })),
  on(loadTenantSettingsFailure, (state, { error }) => ({
    ...state,
    tenantSettingsError: error,
    tenantSettingsIsLoading: false,
    tenantSettings: undefined,
  })),
  on(loadUserPreferences, (state) => ({ ...state, userPreferencesIsLoading: true })),
  on(loadUserPreferencesSuccess, (state, { preferences }) => ({
    ...state,
    userPreferencesIsLoading: false,
    themePreference: preferences.theme ?? ThemePreference.Auto,
  })),
  on(loadUserPreferencesFailure, (state, { error }) => ({
    ...state,
    userPreferencesIsLoading: false,
    userPreferencesError: error,
    themePreference: state.themePreference ?? ThemePreference.Auto,
  })),
  on(saveUserPreferences, (state) => ({ ...state, isSavingUserPreferences: true })),
  on(saveUserPreferencesSuccess, (state, { preferences }) => ({
    ...state,
    isSavingUserPreferences: false,
    themePreference: preferences.theme ?? state.themePreference ?? ThemePreference.Auto,
  })),
  on(saveUserPreferencesFailure, (state, { error }) => ({
    ...state,
    isSavingUserPreferences: false,
    userPreferencesError: error,
  })),
  on(setThemePreference, (state, { themePreference }) => ({ ...state, themePreference })),
  on(setSystemPrefersDark, (state, { systemPrefersDark }) => ({ ...state, systemPrefersDark }))
);
